/**
 * @fullvsl/backend/vsl-factory — VSL project construction with MVP defaults (FUL-5).
 *
 * Applies documented defaults (PRD §9, contracts §1/§2) so a `CreateVslRequest` with
 * only the required fields yields a valid `VslProject`. Defaults are reversible and
 * overridable through the update/config endpoints.
 */
import type {
  AutoplayConfig,
  CreateVslRequest,
  CtaConfig,
  ID,
  ISODate,
  PublishConfig,
  ResumeConfig,
  VslProject,
} from "../../contracts/src/contracts";
import { DEFAULT_PROGRESS_BAR_CONFIG } from "../../contracts/src/contracts";
import { createDefaultOverlays } from "../../contracts/src/overlay-templates";

export const DEFAULT_AUTOPLAY: AutoplayConfig = {
  enabled: true,
  muted: true,
  promptToUnmute: true,
  trackSeparately: true,
};

/** CTO default: basedOn maxWatched, reveal at 50%, persist after reveal. */
export const DEFAULT_CTA: CtaConfig = {
  revealMode: "percent",
  revealAt: 50,
  basedOn: "maxWatched",
  rehideOnRewind: false,
  persistAfterReveal: true,
  destination: { type: "url", value: "" },
};

export const DEFAULT_RESUME: ResumeConfig = { mode: "ask", thresholdSec: 10 };

export const DEFAULT_BRAND_COLOR = "#00685f";
export const DEFAULT_EMIT_ANALYTICS_TO_PARENT = true;

export const DEFAULT_PUBLISH: PublishConfig = { embedScript: "" };

export interface FactoryDeps {
  /** UUID generator seam (defaults to crypto.randomUUID when available). */
  newId?: () => ID;
  now?: () => ISODate;
}

/**
 * Build a full VslProject from a create request, filling defaults for every
 * optional config block. `id`/timestamps are assigned here.
 */
export function createVslProject(
  req: CreateVslRequest,
  deps: FactoryDeps = {},
): VslProject {
  const newId =
    deps.newId ??
    (() => {
      const c = (globalThis as { crypto?: { randomUUID: () => string } }).crypto;
      if (c && typeof c.randomUUID === "function") return c.randomUUID();
      // Fallback (non-crypto envs only); uuid v4 shape.
      return "00000000-0000-4000-8000-" + Math.random().toString(16).slice(2, 16).padEnd(12, "0");
    });
  const now = deps.now ?? (() => new Date().toISOString());

  const ts = now();
  return {
    id: newId(),
    name: req.name,
    status: "draft",
    sourceType: req.sourceType,
    sourceUrl: req.sourceUrl,
    aspectRatio: req.aspectRatio,
    customRatio: req.customRatio,
    autoplay: { ...DEFAULT_AUTOPLAY, ...req.autoplay },
    progressBar: { ...DEFAULT_PROGRESS_BAR_CONFIG, ...req.progressBar },
    cta: { ...DEFAULT_CTA, ...req.cta },
    overlays: createDefaultOverlays(),
    delays: [],
    resume: { ...DEFAULT_RESUME, ...req.resume },
    brandColor: req.brandColor ?? DEFAULT_BRAND_COLOR,
    emitAnalyticsToParent: req.emitAnalyticsToParent ?? DEFAULT_EMIT_ANALYTICS_TO_PARENT,
    publish: { ...DEFAULT_PUBLISH },
    createdAt: ts,
    updatedAt: ts,
  };
}
